/**
 * useReminders — schedules client-side alarm timers for all upcoming reminders.
 *
 * Works entirely in the main thread so alarms fire reliably while the page is
 * open (including with zero network connectivity — the Notifications API is
 * local-only). When the network comes back the pending "mark fired" ops are
 * flushed automatically.
 *
 * Design notes:
 *  - Notification permission is requested once; denied → in-app sonner toast only.
 *  - Dedup source of truth is the SERVER `fired` flag. Within a single page
 *    session an in-memory Set prevents a reminder from firing twice before the
 *    server flag round-trips. We deliberately do NOT persist fired ids to
 *    localStorage: dev DB resets reuse low ids, so a stale persisted set would
 *    silently suppress brand-new reminders forever (the bug this replaced).
 *  - The "mark fired on server" call is queued in localStorage when offline and
 *    flushed on the `online` event (lightweight — no RxDB needed for this).
 *  - Timers are cleared and rescheduled whenever the reminder list changes
 *    (create / delete triggers a `refetch`). A periodic re-evaluation plus a
 *    refetch on tab focus/visibility catches reminders that came due while the
 *    tab was backgrounded (browsers throttle/suspend setTimeout in hidden tabs).
 */
import { useEffect, useRef, useCallback } from 'react'
import { toast } from 'sonner'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/utils/api'
import type { TaskReminder } from '@/utils/api'
import { authStore } from '@/store/auth'
import { queryClient } from '@/lib/queryClient'
import { qk } from '@/lib/queryKeys'

const FIRE_QUEUE_KEY = 'granula_fire_queue'       // localStorage: number[] (ids)
const LEGACY_FIRED_KEY = 'granula_fired_reminders' // removed — clean up stragglers
const MAX_FUTURE_MS = 7 * 24 * 60 * 60 * 1000    // only schedule reminders within 7 days
const REEVAL_INTERVAL_MS = 30 * 1000             // re-check cached reminders every 30s

// In-memory, per-page-session guard against double-firing. Intentionally NOT
// persisted — the server `fired` flag is the durable source of truth.
const firedThisSession = new Set<string>()

// ── localStorage helpers (fire queue only) ────────────────────────────────────

function getFireQueue(): number[] {
  try {
    const raw = localStorage.getItem(FIRE_QUEUE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function enqueueFireOp(id: number) {
  try {
    const q = getFireQueue()
    if (!q.includes(id)) {
      q.push(id)
      localStorage.setItem(FIRE_QUEUE_KEY, JSON.stringify(q))
    }
  } catch { /* ignore */ }
}

function removeFromFireQueue(id: number) {
  try {
    const q = getFireQueue().filter((x) => x !== id)
    localStorage.setItem(FIRE_QUEUE_KEY, JSON.stringify(q))
  } catch { /* ignore */ }
}

// ── Notification permission ───────────────────────────────────────────────────

let _permissionRequested = false

async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied'
  if (Notification.permission !== 'default') return Notification.permission
  if (_permissionRequested) return Notification.permission
  _permissionRequested = true
  return Notification.requestPermission()
}

// ── Fire a single reminder ────────────────────────────────────────────────────

function fireReminder(reminder: TaskReminder, permission: NotificationPermission) {
  // Guard against double-fire within this session (e.g. timer + re-eval race).
  if (firedThisSession.has(String(reminder.id))) return
  firedThisSession.add(String(reminder.id))

  const title = reminder.task?.title ?? 'Task reminder'
  const body = `Reminder for: ${title}`

  // In-app toast (always, even if notifications denied)
  toast(title, {
    description: body,
    duration: 10_000,
    action: {
      label: 'View',
      onClick: () => {
        if (reminder.task?.public_id) {
          window.location.hash = ''
          window.history.pushState({}, '', `/app/work/tasks/${reminder.task.public_id}`)
          window.dispatchEvent(new PopStateEvent('popstate'))
        }
      },
    },
  })

  // System notification
  if (permission === 'granted') {
    try {
      new Notification(title, {
        body,
        icon: '/icon-192.png',
        tag: `reminder-${reminder.id}`,
      })
    } catch { /* denied at OS level */ }
  }

  // Persist the "mark fired" op; flush immediately if online
  enqueueFireOp(reminder.id)
  flushFireQueue().catch(() => {})
}

// ── Flush the fire queue to the server ───────────────────────────────────────

export async function flushFireQueue(): Promise<void> {
  if (!navigator.onLine) return
  const token = authStore.getState().accessToken
  if (!token) return

  const queue = getFireQueue()
  let flushed = 0
  for (const id of queue) {
    try {
      await apiClient.markReminderFired(id)
      removeFromFireQueue(id)
      flushed++
    } catch { /* leave in queue for next flush */ }
  }
  // A fired reminder moves from the Upcoming bucket to the Past bucket in the
  // My Work → Reminders tab. That tab reads through React Query, which has no
  // way to know the flag flipped, so nudge it.
  if (flushed) queryClient.invalidateQueries({ queryKey: qk.reminders() })
}

// ── Main hook ─────────────────────────────────────────────────────────────────

export function useReminders() {
  const user = authStore((s) => s.user)
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const permissionRef = useRef<NotificationPermission>('default')
  // Latest reminder list, so periodic/visibility handlers can re-evaluate
  // without waiting for a new fetch.
  const remindersRef = useRef<TaskReminder[]>([])

  const { data, refetch } = useApi(
    () => apiClient.listReminders().then((r) => r.items),
    [user?.id],
    { skip: !user },
  )

  // One-time cleanup of the removed persisted fired-set (see Design notes).
  useEffect(() => {
    try { localStorage.removeItem(LEGACY_FIRED_KEY) } catch { /* ignore */ }
  }, [])

  // Request permission once when user is logged in
  useEffect(() => {
    if (!user) return
    requestNotificationPermission().then((p) => { permissionRef.current = p })
  }, [user])

  // Flush any queued fire ops on reconnect
  useEffect(() => {
    const handler = () => flushFireQueue()
    window.addEventListener('online', handler)
    return () => window.removeEventListener('online', handler)
  }, [])

  // Reschedule when another part of the app creates or deletes a reminder
  useEffect(() => {
    const handler = () => { void refetch() }
    window.addEventListener('granula:reminders:changed', handler)
    return () => window.removeEventListener('granula:reminders:changed', handler)
  }, [refetch])

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t))
    timersRef.current.clear()
  }, [])

  const scheduleReminders = useCallback((reminders: TaskReminder[]) => {
    clearTimers()
    const now = Date.now()

    for (const r of reminders) {
      // Skip already-fired: server flag (durable) or this session's guard.
      if (r.fired || firedThisSession.has(String(r.id))) continue

      const normalized = /Z|[+-]\d{2}:\d{2}$/.test(r.remind_at) ? r.remind_at : r.remind_at + 'Z'
      const fireAt = new Date(normalized).getTime()
      if (Number.isNaN(fireAt)) continue
      const delay = fireAt - now

      // Due now or overdue (came due while the tab was closed/backgrounded) →
      // fire immediately. The user set this reminder and was never told; show it
      // once. firedThisSession + the server `fired` flag prevent repeats.
      if (delay <= 0) {
        fireReminder(r, permissionRef.current)
        continue
      }

      // Too far in the future — skip (will reschedule on next fetch within range).
      if (delay > MAX_FUTURE_MS) continue

      const tid = setTimeout(() => {
        fireReminder(r, permissionRef.current)
        timersRef.current.delete(r.id)
      }, delay)

      timersRef.current.set(r.id, tid)
    }

    // Flush any ops enqueued above (the 'online' event won't fire if we're
    // already online).
    flushFireQueue().catch(() => {})
  }, [clearTimers])

  // Schedule whenever fresh data arrives.
  useEffect(() => {
    if (data) {
      remindersRef.current = data
      scheduleReminders(data)
    }
    return clearTimers
  }, [data, scheduleReminders, clearTimers])

  // Catch-up: backgrounded tabs throttle/suspend setTimeout, so a reminder can
  // come due without its timer firing. Re-evaluate cached reminders on a short
  // interval, and refetch when the tab regains focus/visibility.
  useEffect(() => {
    const reevaluate = () => {
      if (remindersRef.current.length) scheduleReminders(remindersRef.current)
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetch()
    }
    const interval = setInterval(reevaluate, REEVAL_INTERVAL_MS)
    window.addEventListener('focus', reevaluate)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', reevaluate)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [scheduleReminders, refetch])

  // Expose refetch so callers can reschedule after create/delete
  return { refetch }
}
